// src/components/Sidebar.jsx
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  FaUserEdit,
  FaStore,
  FaUsers,
  FaTruck,
  FaMoneyBill,
  FaPrint,
  FaCommentDots,
  FaSignOutAlt,
  FaGlobe,
  FaUtensils,
  FaFileInvoice,
  FaQrcode,
  FaChartPie,
  FaBoxOpen,
  FaBars,
  FaBell,
  FaEnvelope,
  FaTimes,
  FaChevronDown,
  FaChevronRight,
  FaCodeBranch,
  FaListAlt,
  FaChair,
  FaLock,
  FaCashRegister,
  FaClipboardList,
} from "react-icons/fa";
import logo from "../../src/assets/logiscreen.png";
import LogoIcon from "../assets/Logo";
import { GrProjects } from "react-icons/gr";
import { empty, getUserdata, logoutUser } from "../helper/generalHelper";
import BusinessModal from "./restaurant/restaurants/BusinessModal";
import { useLanguages } from "../hooks/useLanguages";

const Sidebar = ({ isOpen, onClose }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const [openDropdown, setOpenDropdown] = useState(null);
  const { findLanguageByCode } = useLanguages();

  const locked = empty(getUserdata("restaurantId"));

  const businessItems = [
    { label: "Business", icon: <FaStore />, path: "/restaurant/business" },
    // { label: "Branches", icon: <FaCodeBranch />, path: "/restaurant/branches" },
  ];

  const menuItems = [
    {
      label: "Menus",
      icon: <FaListAlt />,
      path: "/restaurant/menus",
      subMenu: [
        { label: "Add Menu", path: "/restaurant/menus/add" },
        { label: "All Menus", path: "/restaurant/menus/all" },
        { label: "Category", path: "/restaurant/menus/category" },
      ],
    },
    { label: "Tables", icon: <FaChair />, path: "/restaurant/tables" },
    { label: "Inventory", icon: <FaBoxOpen />, path: "/restaurant/inventory" },
    { label: "Customer", icon: <FaUsers />, path: "/restaurant/customers" },
    { label: "Vendor", icon: <FaUsers />, path: "/restaurant/vendors" },
    { label: "Staff", icon: <FaUsers />, path: "/restaurant/staff" },
    // { label: "Delivery", icon: <FaTruck />, path: "/delivery" },
    {
      label: "Expense / Income",
      icon: <FaMoneyBill />,
      path: "/restaurant/expenseincome",
    },
    // { label: "Print Management", icon: <FaPrint />, path: "/print-management" },
  ];

  const alwaysAccessibleItems = [
    { label: "Help Chat", icon: <FaCommentDots />, path: "/restaurant/help" },
  ];

  const settingsItems = [
    {
      label: `Language - ${
        findLanguageByCode(getUserdata("lang") || "en")?.name || "English"
      }`,
      icon: <FaGlobe />,
      path: "/restaurant/language",
    },
  ];

  const appsItems = [
    { label: "Counter (POS)", icon: <FaCashRegister />, path: "/restaurant/counter" },
    { label: "KOT - Display", icon: <FaUtensils />, path: "/restaurant/kot" },
    { label: "Delivery / Dispatch", icon: <FaTruck />, path: "/restaurant/dispatch" },
    { label: "Orders", icon: <FaClipboardList />, path: "/restaurant/orders" },
    { label: "Receipt", icon: <FaFileInvoice />, path: "/restaurant/receipt" },
  ];

  const toggleDropdown = (index) => {
    if (locked) return; // Prevent dropdown toggle when locked
    setOpenDropdown(openDropdown === index ? null : index);
  };

  // Render menu item with lock condition
  const renderMenuItem = (item, index, section = "management") => {
    const isLocked = locked && section !== "always";

    return (
      <div key={index}>
        {item.subMenu ? (
          <>
            <button
              onClick={() => toggleDropdown(index)}
              className={`w-full flex items-center justify-between text-sm p-2 rounded transition-all ${
                location.pathname.startsWith(item.path) && !isLocked
                  ? "bg-rose-600 text-white"
                  : isLocked
                  ? "bg-gray-50 text-gray-400 cursor-not-allowed"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
              disabled={isLocked}
            >
              <div className="flex items-center gap-2">
                {item.icon}
                <span>{item.label}</span>
              </div>
              {isLocked ? (
                <FaLock className="text-rose-500" size={12} />
              ) : openDropdown === index ? (
                <FaChevronDown size={12} />
              ) : (
                <FaChevronRight size={12} />
              )}
            </button>

            {!isLocked && openDropdown === index && (
              <div className="ml-6 mt-1 space-y-1">
                {item.subMenu.map((sub, j) => (
                  <Link
                    key={j}
                    to={sub.path}
                    onClick={() => {
                      setMobileMenuOpen(false);
                      if (onClose) onClose();
                    }}
                    className={`block text-sm p-2 rounded transition-all ${
                      location.pathname === sub.path
                        ? "bg-rose-500 text-white"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    {sub.label}
                  </Link>
                ))}
              </div>
            )}
          </>
        ) : (
          <Link
            to={isLocked ? "#" : item.path}
            className={`w-full flex items-center justify-between text-sm p-2 rounded transition-all ${
              (location.pathname === item.path || 
                (item.path === "/restaurant/counter" && (location.pathname.startsWith("/restaurant/create-order") || location.pathname.startsWith("/restaurant/table-selection"))) ||
                (item.path === "/restaurant/staff" && (location.pathname.startsWith("/restaurant/staff") || location.pathname.startsWith("/restaurant/roles-management") || location.pathname.startsWith("/restaurant/add-staff")))
              ) && !isLocked
                ? "bg-rose-600 text-white"
                : isLocked
                ? "bg-gray-50 text-gray-400 cursor-not-allowed pointer-events-none"
                : "text-gray-700 hover:bg-gray-100"
            }`}
            onClick={(e) => {
              if (isLocked) {
                e.preventDefault();
              } else {
                setMobileMenuOpen(false);
                if (onClose) onClose();
              }
            }}
          >
            <div className="flex items-center gap-2">
              {item.icon}
              <span>{item.label}</span>
            </div>
            {isLocked && <FaLock className="text-rose-500" size={12} />}
          </Link>
        )}
      </div>
    );
  };

  // Render section with lock condition
  const renderSection = (title, items, sectionType = "management") => (
    <div className="mt-6">
      <p className="font-semibold text-sm mb-2">{title}</p>
      <div className="space-y-1">
        {items.map((item, index) => renderMenuItem(item, index, sectionType))}
      </div>
    </div>
  );

  return (
    <>
      {/* ✅ Mobile Top Navbar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-white shadow flex items-center justify-between px-4 py-2">
        {localStorage.getItem("restaurantImage") ? (
          <img src={localStorage.getItem("restaurantImage")} alt="Business Logo" className="h-8 w-8 object-cover rounded-full border shadow-sm" />
        ) : (
          <img src={logo} alt="Logo" className="h-8" />
        )}
        <div className="flex items-center gap-4 text-gray-700 text-lg">
          <FaEnvelope className="cursor-pointer hover:text-rose-600" />
          <FaBell className="cursor-pointer hover:text-rose-600" />
          <button onClick={() => setMobileMenuOpen(true)}>
            <FaBars />
          </button>
        </div>
      </div>

      {/* ✅ Mobile Backdrop Overlay */}
      {(isOpen || mobileMenuOpen) && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => {
            setMobileMenuOpen(false);
            if (onClose) onClose();
          }}
        />
      )}

      {/* ✅ Sidebar (desktop or mobile slide-in) */}
      <div
        id="sidebar-content"
        className={`fixed top-0 left-0 md:h-full h-[95%] overflow-scroll w-72 bg-white shadow-lg z-50 transition-transform ${
          isOpen || mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        } md:relative md:translate-x-0`}
      >
        {/* Mobile Close Button */}
        <div className="md:hidden flex justify-end p-2">
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="text-gray-700"
          >
            <FaTimes size={20} />
          </button>
        </div>

        {/* Sidebar Content */}
        <div className="p-4 border-b">
          {localStorage.getItem("restaurantImage") ? (
            <img 
              src={localStorage.getItem("restaurantImage")} 
              alt="Business Logo" 
              className="h-16 w-16 object-cover mb-2 rounded-full border shadow-sm"
            />
          ) : (
            <LogoIcon />
          )}
          <h1 className="text-lg font-bold mt-3">Stellaris</h1>
          <p className="text-sm text-gray-500">Serve More. Stress Less.</p>
        </div>

        <div className="p-4 space-y-2">
          {/* Business Section - Always accessible */}
          <BusinessModal />
  <div className="w-full my-2">
            <Link
              to="/restaurant/subscriptionplans"
              onClick={() => {
                setMobileMenuOpen(false);
                if (onClose) onClose();
              }}
              className="w-full flex justify-center items-center gap-2 py-2 bg-stone-950 text-white text-xs rounded transition-all hover:bg-stone-800"
            >
              <GrProjects size={12} />
              Subscription Plans
            </Link>
          </div>
          {/* Lock Warning Banner */}
          {locked && (
            <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <FaLock
                  className="text-amber-500 mt-0.5 flex-shrink-0"
                  size={14}
                />
                <div>
                  <p className="text-xs font-medium text-amber-800">
                    Business Setup Required
                  </p>
                  <p className="text-xs text-amber-600 mt-1">
                    Complete your business profile to unlock all features
                  </p>
                </div>
              </div>
            </div>
          )}

        

          {/* Business & Branches - Always accessible */}
          {renderSection("Enterprise", businessItems, "always")}

          {/* Management Section - Locked when restricted */}
          {renderSection("Management", menuItems, "management")}

          {/* Others Section - Always accessible */}
          <div className="mt-6">
            <p className="font-semibold text-sm mb-2">Others</p>
            <div className="space-y-1">
              {alwaysAccessibleItems.map((item, i) => (
                <Link
                  to={item.path}
                  key={i}
                  onClick={() => {
                    setMobileMenuOpen(false);
                    if (onClose) onClose();
                  }}
                  className={`flex items-center gap-2 text-sm p-2 rounded ${
                    location.pathname === item.path
                      ? "bg-rose-600 text-white"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  {item.icon} {item.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Apps Section - Locked when restricted */}
          {renderSection("Apps", appsItems, "management")}

          {/* Settings Section - Locked when restricted */}
          <div className="mt-6">
            <p className="font-semibold text-sm mb-2">Settings</p>
            <div className="space-y-1">
              {settingsItems.map((item, index) =>
                renderMenuItem(item, index, "management")
              )}

              {/* Logout - Always accessible */}
              <div
                onClick={() => logoutUser(false)}
                className={`flex items-center justify-between text-sm p-2 rounded cursor-pointer transition-all text-gray-700 hover:bg-gray-100`}
              >
                <div className="flex items-center gap-2">
                  <FaSignOutAlt />
                  <span>Logout</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;